import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2, Info } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { useProgressiveNerv } from '@/hooks/useProgressiveNerv';
import { useSyncCompletionCheck } from '@/hooks/useSyncCompletionCheck';
import { useFinancialSnapshotProcessing } from '@/hooks/useFinancialSnapshotProcessing';
import { Alert, AlertDescription } from '@/components/ui/alert';
import DailyNervGraph from './DailyNervGraph';
import { invokeWithAbort } from '@/utils/invokeWithAbort';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';

interface DailyNervViewProps {
  organisation: any;
}

const DailyNervView: React.FC<DailyNervViewProps> = ({ organisation }) => {
  const skuAbortRef = useRef<AbortController | null>(null);

  // Abort SKU fetch on unmount (tab switch)
  useEffect(() => {
    return () => { skuAbortRef.current?.abort(); };
  }, []);

  // Compute smart default: prefer API connection over CSV when both exist for same platform
  const smartDefaultSource = React.useMemo(() => {
    if (!organisation?.connections) return 'All';
    const connections = Array.isArray(organisation.connections)
      ? organisation.connections
      : Object.values(organisation.connections);

    const apiPlatforms = new Set<string>();
    const csvPlatforms = new Set<string>();

    connections.forEach((conn: any) => {
      if (!conn || conn.service === 'file') return;
      if (conn.type === 'CSV' || conn.connectionId?.startsWith('csv-import-')) {
        if (conn.ecommerce_subtype) csvPlatforms.add(conn.ecommerce_subtype.toLowerCase());
      } else {
        const service = (conn.service || '').toLowerCase();
        if (service.includes('amazon')) apiPlatforms.add('amazon');
        else if (service.includes('shopify')) apiPlatforms.add('shopify');
      }
    });

    // If a platform has both API and CSV, default to just the API source
    for (const platform of apiPlatforms) {
      if (csvPlatforms.has(platform)) {
        return platform; // e.g. 'amazon' or 'shopify'
      }
    }
    return 'All';
  }, [organisation?.connections]);

  // Price source filter - controlled by UI dropdown
  const [selectedSource, setSelectedSource] = useState<string>(smartDefaultSource);

  // Get inventory source from org config (read-only display)
  const inventorySourceLabel = React.useMemo(() => {
    const config = organisation?.inventory_source_config;
    if (!config?.primary_source || config.primary_source === 'all') return null;
    // Format the source name nicely
    const source = config.primary_source;
    if (source.includes('csv_import')) {
      const platform = source.replace('_csv_import', '');
      return `${platform.charAt(0).toUpperCase() + platform.slice(1)} CSV`;
    }
    if (source.endsWith('_csv')) {
      const platform = source.replace('_csv', '');
      return `${platform.charAt(0).toUpperCase() + platform.slice(1)} CSV`;
    }
    return source.charAt(0).toUpperCase() + source.slice(1);
  }, [organisation?.inventory_source_config]);
  // Check if all Fivetran syncs have completed for today
  const { latestSyncedDate, allSynced, loading: syncCheckLoading } = useSyncCompletionCheck(organisation);


  const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date | null }>({
    startDate: subDays(new Date(), 7),
    endDate: null
  });

  // Set end date once sync check completes
  useEffect(() => {
    if (!syncCheckLoading && dateRange.endDate === null) {
      setDateRange(prev => ({ ...prev, endDate: latestSyncedDate }));
    }
  }, [latestSyncedDate, syncCheckLoading, dateRange.endDate]);
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [skuOptions, setSkuOptions] = useState<string[]>([]);
  const [skuSearch, setSkuSearch] = useState('');
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [skuOffset, setSkuOffset] = useState(0);
  const [hasMoreSkus, setHasMoreSkus] = useState(true);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const minDate = React.useMemo(() => {
    if (organisation?.snapshots_start_date) {
      return new Date(`${organisation.snapshots_start_date}T00:00:00`);
    }
    return subDays(new Date(), 365);
  }, [organisation?.snapshots_start_date]);

  const isStartBeforeSnapshots = React.useMemo(() => {
    if (!organisation?.snapshots_start_date) return false;
    return dateRange.startDate < minDate;
  }, [dateRange.startDate, minDate, organisation?.snapshots_start_date]);

  // Extract available sources from organisation connections
  const availableSources = React.useMemo(() => {
    const sources: { value: string; label: string }[] = [
      { value: 'All', label: 'All Sources' }
    ];

    if (organisation?.connections) {
      const connections = Array.isArray(organisation.connections)
        ? organisation.connections
        : Object.values(organisation.connections);

      connections.forEach((conn: any) => {
        if (!conn) return;

        // Skip file-type connections
        if (conn.service === 'file') return;

        // Get display name and filter value
        let label = conn.name || '';
        let value = '';

        // For CSV imports, use ecommerce_subtype to generate descriptive label
        if (conn.type === 'CSV' || conn.connectionId?.startsWith('csv-import-')) {
          if (conn.ecommerce_subtype) {
            const subtype = conn.ecommerce_subtype.toLowerCase();
            value = `${subtype}_csv_import`; // e.g., "amazon_csv_import"
            // Always use the generated label for CSV imports to show the platform name
            label = `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} CSV Import`;
          }
        } else {
          // For Fivetran connections
          const service = (conn.service || '').toLowerCase();
          if (service.includes('shopify')) {
            value = 'shopify';
            label = label || 'Shopify';
          } else if (service.includes('amazon')) {
            value = 'amazon';
            label = label || 'Amazon';
          }
        }

        if (value && !sources.find(s => s.value === value)) {
          sources.push({ value, label });
        }
      });
    }

    return sources;
  }, [organisation?.connections]);

  // Pass the selected source to the hook
  const sources = React.useMemo(() => {
    return selectedSource === 'All' ? [] : [selectedSource];
  }, [selectedSource]);

  // Fetch available SKUs
  const fetchSkus = useCallback(async (search: string = '', offset: number = 0) => {
    if (!organisation?.id) return;

    // Abort previous SKU fetch
    skuAbortRef.current?.abort();
    const controller = new AbortController();
    skuAbortRef.current = controller;

    setLoadingSkus(true);
    try {
      const { data, error } = await invokeWithAbort('fetch-nerv-skus-v3', {
        organisationId: organisation.id,
        search,
        limit: 10,
        offset,
        sources: selectedSource === 'All' ? [] : [selectedSource]
      }, controller.signal);

      if (error) throw error;
      if (controller.signal.aborted) return;

      if (offset === 0) {
        setSkuOptions(data.skus || []);
      } else {
        setSkuOptions(prev => [...prev, ...(data.skus || [])]);
      }

      setHasMoreSkus(data.hasMore ?? false);
    } catch (error: any) {
      if (error?.name === 'AbortError' || controller.signal.aborted) return;
      console.error('Error fetching SKUs:', error);
    } finally {
      setLoadingSkus(false);
    }
  }, [organisation, selectedSource]);

  // Initial SKU fetch
  useEffect(() => {
    setSkuOffset(0);
    fetchSkus('', 0);
  }, [fetchSkus]);


  // Load more SKUs
  const loadMoreSkus = useCallback(() => {
    if (!loadingSkus && hasMoreSkus) {
      const newOffset = skuOffset + 10;
      setSkuOffset(newOffset);
      fetchSkus(skuSearch, newOffset);
    }
  }, [loadingSkus, hasMoreSkus, skuOffset, skuSearch, fetchSkus]);

  // Debounced SKU fetch
  const debouncedFetchSkus = useCallback(
    (() => {
      let timer: ReturnType<typeof setTimeout>;
      return (search: string) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          setSkuOffset(0);
          fetchSkus(search, 0);
        }, 400);
      };
    })(),
    [fetchSkus]
  );

  // Search SKUs
  const handleSkuSearch = useCallback((search: string) => {
    setSkuSearch(search);
    debouncedFetchSkus(search);
  }, [debouncedFetchSkus]);

  // Toggle SKU selection
  const toggleSku = useCallback((sku: string) => {
    setSelectedSkus(prev => {
      if (prev.includes(sku)) {
        return prev.filter(s => s !== sku);
      } else {
        if (prev.length >= 10) {
          return prev; // Max 10 SKUs
        }
        return [...prev, sku];
      }
    });
  }, []);

  // Remove SKU
  const removeSku = useCallback((sku: string) => {
    setSelectedSkus(prev => prev.filter(s => s !== sku));
  }, []);

  // Guard: only fetch when endDate is resolved
  const resolvedDateRange = React.useMemo(() => {
    const endDate = dateRange.endDate ?? subDays(new Date(), 2);
    const startDate = dateRange.startDate < minDate ? minDate : dateRange.startDate;
    return { startDate, endDate };
  }, [dateRange.startDate, dateRange.endDate, minDate]);

  const diffDays = React.useMemo(() => {
    const end = dateRange.endDate ?? subDays(new Date(), 2);
    return Math.ceil((end.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24));
  }, [dateRange.startDate, dateRange.endDate]);

  // Fetch NERV data using the hook
  const { data, isLoading: nervLoading, backfilling, error, tableNotFound, emptyReason, refetch } = useProgressiveNerv({
    organisation,
    sources,
    dateRange: resolvedDateRange,
    selectedSkus
  });

  const { isProcessing: snapshotProcessing, hasFailed: snapshotFailed } = useFinancialSnapshotProcessing(
    organisation?.id,
    refetch,
  );

  const isLoading = !dateRange.endDate || nervLoading;

  // True when startDate has no data — a full-range historical backfill was triggered
  // but the workers haven't finished yet. User needs to wait and refresh.
  const hasHistoricalGap = React.useMemo(() => {
    if (!data || data.length === 0 || diffDays <= 30) return false;
    const startStr = format(resolvedDateRange.startDate, 'yyyy-MM-dd');
    const startRow = data.find((r: any) => r.date === startStr);
    return !startRow || startRow.current === null;
  }, [data, resolvedDateRange.startDate, diffDays]);

  // Sort SKU options: selected first, then alphabetically
  const sortedSkuOptions = React.useMemo(() => {
    // Ensure all selected SKUs are included, even if not in fetched options
    const allSkus = new Set([...selectedSkus, ...skuOptions]);
    return Array.from(allSkus).sort((a, b) => {
      const aSelected = selectedSkus.includes(a);
      const bSelected = selectedSkus.includes(b);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.localeCompare(b);
    });
  }, [skuOptions, selectedSkus]);

  // If the snapshot table doesn't exist, don't render anything
  if (tableNotFound) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily NERV Trends</CardTitle>
        <CardDescription>
          Track NERV over time for selected Products
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap gap-4 items-end">
            {/* Product Multi-Select */}
            <div className="flex-1 min-w-[200px] max-w-md">
              <label className="text-sm font-medium mb-2 block">
                Select Products (optional, max 10)
              </label>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      "w-full justify-between",
                      !selectedSkus.length && "text-muted-foreground"
                    )}
                  >
                    {selectedSkus.length > 0
                      ? `${selectedSkus.length} Product${selectedSkus.length > 1 ? 's' : ''} selected`
                      : "All Products"}
                    <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search Products..."
                      value={skuSearch}
                      onValueChange={handleSkuSearch}
                    />
                    <CommandEmpty>
                      {loadingSkus ? 'Loading...' : 'No Product found.'}
                    </CommandEmpty>
                    <CommandGroup className="max-h-[300px] overflow-auto">
                      {sortedSkuOptions.map((sku) => {
                        const isSelected = selectedSkus.includes(sku);
                        const canSelect = selectedSkus.length < 10 || isSelected;

                        return (
                          <CommandItem
                            key={sku}
                            value={sku}
                            onSelect={() => canSelect && toggleSku(sku)}
                            disabled={!canSelect}
                            className={cn(!canSelect && "opacity-50 cursor-not-allowed")}
                          >
                            <Checkbox
                              checked={isSelected}
                              className="mr-2"
                            />
                            <span className={cn(isSelected && "font-semibold")}>{sku}</span>
                            {isSelected && (
                              <Badge variant="secondary" className="ml-auto">
                                Selected
                              </Badge>
                            )}
                          </CommandItem>
                        );
                      })}
                      {hasMoreSkus && (
                        <CommandItem
                          onSelect={loadMoreSkus}
                          className="justify-center text-muted-foreground"
                        >
                          {loadingSkus ? 'Loading...' : 'Load more...'}
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>

              {selectedSkus.length >= 10 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum 10 Products selected
                </p>
              )}
            </div>

            {/* Source Filter */}
            <div className="w-[200px]">
              <label className="text-sm font-medium mb-2 block">Data Source</label>
              <Select value={selectedSource} onValueChange={setSelectedSource}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableSources.map((source) => (
                    <SelectItem key={source.value} value={source.value}>
                      {source.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="flex gap-2">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Date</label>
                <Popover open={startPickerOpen} onOpenChange={setStartPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[140px] justify-start"
                      disabled={!dateRange.endDate}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(dateRange.startDate, 'MMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.startDate}
                      onSelect={(date) => {
                        if (date) {
                          setDateRange(prev => ({ ...prev, startDate: date }));
                          setStartPickerOpen(false);
                        }
                      }}
                      fromDate={minDate}
                      toDate={dateRange.endDate ?? latestSyncedDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">End Date</label>
                <Popover open={endPickerOpen} onOpenChange={setEndPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[140px] justify-start"
                      disabled={!dateRange.endDate}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.endDate ? format(dateRange.endDate, 'MMM d, yyyy') : '...'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.endDate ?? undefined}
                      onSelect={(date) => {
                        if (date) {
                          setDateRange(prev => ({ ...prev, endDate: date }));
                          setEndPickerOpen(false);
                        }
                      }}
                      fromDate={dateRange.startDate}
                      toDate={latestSyncedDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Inventory Source Badge */}
            {inventorySourceLabel && (
              <div className="flex items-end">
                <Badge variant="outline" className="text-xs whitespace-nowrap">
                  Inventory from: {inventorySourceLabel}
                </Badge>
              </div>
            )}
          </div>

          {/* Sync pending banner */}
          {!syncCheckLoading && !allSynced && (
            <Alert variant="default" className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                Waiting for today's sync to complete — showing data through {format(latestSyncedDate, 'MMM d, yyyy')}
              </AlertDescription>
            </Alert>
          )}

          {/* Historical data notes for ranges beyond the 30d fresh window */}
          {isStartBeforeSnapshots && (
            <Alert variant="default" className="border-muted bg-muted/30">
              <Info className="h-4 w-4 text-muted-foreground" />
              <AlertDescription className="text-muted-foreground text-xs">
                The selected start date is earlier than available snapshots. Data is shown from {format(minDate, 'MMM d, yyyy')} onward, and dates before this are not backfilled.
              </AlertDescription>
            </Alert>
          )}
          {hasHistoricalGap && !backfilling && (
            <Alert variant="default" className="border-muted bg-muted/30">
              <Info className="h-4 w-4 text-muted-foreground" />
              <AlertDescription className="text-muted-foreground text-xs">
                NERV history before {format(subDays(resolvedDateRange.endDate, 30), 'MMM d, yyyy')} is being computed in the background. This chart refreshes itself automatically as data becomes available.
              </AlertDescription>
            </Alert>
          )}

          {/* Backfill banner for extended date ranges */}
          {backfilling && (
            <div className="border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-md p-3 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
              {hasHistoricalGap
                ? `Computing historical NERV data for the selected range. This may take a few minutes…`
                : `Results are being computed for the selected date range. This may take a moment.`}
            </div>
          )}

          {/* Sales import processing banner */}
          {snapshotProcessing && (
            <Alert variant="default" className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                Sales data is being processed — the graph will update automatically when complete.
              </AlertDescription>
            </Alert>
          )}
          {snapshotFailed && (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertDescription>
                Sales data processing encountered an error. The graph may not reflect your latest import.
              </AlertDescription>
            </Alert>
          )}

          {/* Graph */}
          <Card>
            <CardContent>
              {error ? (
                <div className="border border-destructive/20 text-destructive rounded-md p-6 text-center flex flex-col items-center gap-3">
                  <span>{error}</span>
                  <Button variant="outline" size="sm" onClick={refetch}>Try again</Button>
                </div>
              ) : isLoading ? (
                <div className="border rounded-md p-6 text-center text-muted-foreground">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading NERV trends...
                  </div>
                </div>
              ) : !data || data.length === 0 ? (
                <div className="border rounded-md p-6 text-center text-muted-foreground">
                  {emptyReason === 'no_inventory_table'
                    ? 'No inventory data has been imported yet. Please import inventory or connect a data source.'
                    : emptyReason === 'no_inventory_data'
                      ? 'No inventory data found for the selected date range. Inventory may not have been synced for these dates.'
                      : emptyReason === 'no_inventory_snapshots'
                        ? 'Inventory snapshots are not enabled for this organisation.'
                        : emptyReason === 'no_financial_snapshots'
                          ? 'Financial snapshots are not enabled for this organisation.'
                          : 'No NERV data available for the selected SKUs and date range.'}
                </div>
              ) : (
                <div className="py-4 relative">
                  {isLoading && (
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-2 bg-background/80 backdrop-blur-sm border rounded-md px-3 py-1.5 text-sm text-muted-foreground shadow-sm">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading new SKU data...
                    </div>
                  )}
                  <DailyNervGraph data={data} organisation={organisation} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyNervView;
